if (!customElements.get('quick-add-modal')) {
  customElements.define('quick-add-modal', class QuickAddModal extends ModalDialog {
    constructor() {
      super();
      this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
      this._modalSwipers = [];
      this._onVariantChangeRef = null;
      this._onOptionChangeRef = null;
    }

    hide(preventFocus = false) {
      const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      if (cartNotification) cartNotification.setActiveElement(this.openedBy);

      // Unbind listeners for this modal instance
      if (this._onVariantChangeRef) {
        this.modalContent.removeEventListener('variant:change', this._onVariantChangeRef);
        this.modalContent.removeEventListener('variant:changed', this._onVariantChangeRef);
        document.removeEventListener('variant:change', this._onVariantChangeRef);
        document.removeEventListener('variant:changed', this._onVariantChangeRef);
        this._onVariantChangeRef = null;
      }
      if (this._onOptionChangeRef) {
        this.modalContent.removeEventListener('change', this._onOptionChangeRef);
        this._onOptionChangeRef = null;
      }

      // Destroy Swipers inside the modal
      this.modalContent.querySelectorAll('.swiper, .swiper-container').forEach(el => {
        if (el.swiper) { try { el.swiper.destroy(true, true); } catch (e) {} }
      });
      this._modalSwipers = [];

      this.modalContent.innerHTML = '';

      if (preventFocus) this.openedBy = null;
      super.hide();
    }

    show(opener) {
      opener.setAttribute('aria-disabled', true);
      opener.classList.add('loading');
      opener.querySelector('.loading-overlay__spinner').classList.remove('hidden');

      fetch(opener.getAttribute('data-product-url'))
        .then(r => r.text())
        .then(responseText => {
          const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');

          // --- inject the FULL section (preserves classes/CSS vars) ---
          const productSection = responseHTML.querySelector('section[id^="MainProduct-"]');
          if (!productSection) throw new Error('Product section not found');

          const originalSectionId = productSection.dataset.section; // remember original id
          let sectionHTML = productSection.outerHTML.replaceAll(originalSectionId, `quickadd-${originalSectionId}`);

          this.setInnerHTML(this.modalContent, sectionHTML);

          // cache inserted section
          this.modalSection = this.modalContent.querySelector('section[id^="MainProduct-"]');

          // <<< IMPORTANT: tell variant components where to read JSON >>>
          this.addOriginalSectionRefs(originalSectionId);

          // Shopify / XR
          if (window.Shopify && Shopify.PaymentButton) Shopify.PaymentButton.init();
          if (window.ProductModel) window.ProductModel.loadShopifyXR();

          // Dawn tweaks
          this.removeGalleryListSemantic();
          this.updateImageSizes();
          this.preventVariantURLSwitching();

          // show first so Swiper can measure
          super.show(opener);

          // modal-only swipers + variant sync
          this.rebuildSwipers();
          this.bindVariantSync();
          this.syncToCurrentVariant();

          try { window.dispatchEvent(new Event('resize')); } catch (e) {}
        })
        .finally(() => {
          opener.removeAttribute('aria-disabled');
          opener.classList.remove('loading');
          opener.querySelector('.loading-overlay__spinner').classList.add('hidden');
        });
    }

    setInnerHTML(element, html) {
      element.innerHTML = html;
      // re-execute inline scripts in injected HTML
      element.querySelectorAll('script').forEach(oldScript => {
        const s = document.createElement('script');
        Array.from(oldScript.attributes).forEach(a => s.setAttribute(a.name, a.value));
        s.appendChild(document.createTextNode(oldScript.innerHTML));
        oldScript.parentNode.replaceChild(s, oldScript);
      });
    }

    preventVariantURLSwitching() {
      const vs = this.modalContent.querySelector('variant-radios,variant-selects');
      if (vs) vs.setAttribute('data-update-url', 'false');
    }

    addOriginalSectionRefs(originalSectionId) {
      // Point variant components/forms to ORIGINAL section id so they find the right JSON
      this.modalContent
        .querySelectorAll('variant-selects, variant-radios, .product-form, product-form')
        .forEach(el => { try { el.dataset.originalSection = originalSectionId; } catch(e) {} });

      const form = this.modalContent.querySelector('form[action^="/cart/add"]');
      if (form) { try { form.dataset.originalSection = originalSectionId; } catch(e) {} }
    }

    removeGalleryListSemantic() {
      const galleryList = this.modalContent.querySelector('[id^="Slider-Gallery"]');
      if (!galleryList) return;
      galleryList.setAttribute('role', 'presentation');
      galleryList.querySelectorAll('[id^="Slide-"]').forEach(li => li.setAttribute('role', 'presentation'));
    }

    updateImageSizes() {
      const product = this.modalContent.querySelector('.product');
      const desktopColumns = product && product.classList.contains('product--columns');
      if (!desktopColumns) return;

      const mediaImages = product.querySelectorAll('.product__media img');
      if (!mediaImages.length) return;

      let sizes = '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';
      if (product.classList.contains('product--medium')) sizes = sizes.replace('715px', '605px');
      else if (product.classList.contains('product--small')) sizes = sizes.replace('715px', '495px');
      mediaImages.forEach(img => img.setAttribute('sizes', sizes));
    }

    /* ---------------- Modal-only Swiper logic ---------------- */

    rebuildSwipers() {
      // clean any existing instances in modal
      this.modalContent.querySelectorAll('.swiper, .swiper-container').forEach(el => {
        if (el.swiper) { try { el.swiper.destroy(true, true); } catch (e) {} }
      });
      this._modalSwipers = [];

      if (!window.Swiper) return;

      const galleries = this.modalContent.querySelectorAll('[id^="Slider-Gallery-"]');
      galleries.forEach((gallery) => {
        const mainEl  = gallery.querySelector('.main-swiper');
        const thumbEl = gallery.querySelector('.thumb-container');
        const nextEl  = gallery.querySelector('.thumb-next');
        const prevEl  = gallery.querySelector('.thumb-prev');
        const pagEl   = gallery.querySelector('.swiper-pagination');

        let thumbSwiper = null;
        if (thumbEl) {
          thumbSwiper = new Swiper(thumbEl, {
            breakpoints: { 991: { direction: 'vertical', slidesPerView: 4 }, 0: { direction: 'horizontal', slidesPerView: 6 } },
            loop: true,
            slideToClickedSlide: true,
            centeredSlidesBounds: true,
            spaceBetween: 5,
            autoHeight: true,
            mousewheel: true,
            navigation: (nextEl && prevEl) ? { nextEl, prevEl } : undefined,
            observer: true,
            observeParents: true,
            observeSlideChildren: true
          });
          this._modalSwipers.push(thumbSwiper);
        }

        if (mainEl) {
          const mainSwiper = new Swiper(mainEl, {
            slidesPerView: 'auto',
            spaceBetween: 3,
            centeredSlides: false,
            loop: false, // predictable indices for variant sync
            autoHeight: false,
            thumbs: thumbSwiper ? { swiper: thumbSwiper } : undefined,
            pagination: pagEl ? { el: pagEl, clickable: true } : undefined,
            observer: true,
            observeParents: true,
            observeSlideChildren: true,
            preloadImages: false,
            lazy: { loadOnTransitionStart: true }
          });
          this._modalSwipers.push(mainSwiper);
        }
      });
    }

    /* -------- Variant → media sync inside modal -------- */

    bindVariantSync() {
      // remove previous handlers
      if (this._onVariantChangeRef) {
        this.modalContent.removeEventListener('variant:change', this._onVariantChangeRef);
        this.modalContent.removeEventListener('variant:changed', this._onVariantChangeRef);
        document.removeEventListener('variant:change', this._onVariantChangeRef);
        document.removeEventListener('variant:changed', this._onVariantChangeRef);
      }
      if (this._onOptionChangeRef) {
        this.modalContent.removeEventListener('change', this._onOptionChangeRef);
      }

      this._onVariantChangeRef = (evt) => {
        const variant = evt?.detail?.variant || evt?.variant || null;
        this.syncToVariant(variant);
      };

      this.modalContent.addEventListener('variant:change', this._onVariantChangeRef);
      this.modalContent.addEventListener('variant:changed', this._onVariantChangeRef);
      document.addEventListener('variant:change', this._onVariantChangeRef);
      document.addEventListener('variant:changed', this._onVariantChangeRef);

      this._onOptionChangeRef = () => {
        const variant = this.readCurrentVariantFromDOM();
        if (variant) this.syncToVariant(variant);
      };
      this.modalContent.addEventListener('change', this._onOptionChangeRef);
    }

    syncToCurrentVariant() {
      const v = this.readCurrentVariantFromDOM();
      if (v) this.syncToVariant(v);
    }

    readCurrentVariantFromDOM() {
      // 1) JSON blob with selected variant
      try {
        const jsonEl = this.modalContent.querySelector('[type="application/json"][data-selected-variant]');
        if (jsonEl) {
          const v = JSON.parse(jsonEl.textContent);
          if (v && v.id) return v;
        }
      } catch (e) {}

      // 2) variant components
      const picker = this.modalContent.querySelector('variant-radios, variant-selects');
      if (picker && picker.currentVariant) return picker.currentVariant;

      // 3) fallback to form id
      const idInput = this.modalContent.querySelector('form[action^="/cart/add"] input[name="id"]');
      if (idInput && idInput.value) return { id: Number(idInput.value) };

      return null;
    }

    syncToVariant(variant) {
      const mediaId = variant?.featured_media?.id;
      if (!mediaId) return;

      // modal section id is prefixed (quickadd-<orig>)
      const sectionId = this.modalContent.querySelector('[data-section]')?.dataset?.section || '';
      const key = `${sectionId}-${mediaId}`;

      const mainEl = this.modalContent.querySelector('.main-swiper');
      if (!mainEl || !mainEl.swiper) return;

      const slides = Array.from(mainEl.querySelectorAll('.swiper-slide'));
      const target = this.modalContent.querySelector(`.main-swiper .swiper-slide[data-media-id="${key}"]`);
      if (!target) return;

      const idx = slides.indexOf(target);
      if (idx < 0) return;

      const useLoop = !!mainEl.swiper.params.loop;
      if (useLoop && typeof mainEl.swiper.slideToLoop === 'function') {
        mainEl.swiper.slideToLoop(idx);
      } else {
        mainEl.swiper.slideTo(idx);
      }

      // thumbs
      const thumbEl = this.modalContent.querySelector('.thumb-container');
      if (thumbEl && thumbEl.swiper) {
        const tSlides = Array.from(thumbEl.querySelectorAll('.swiper-slide'));
        const tTarget = this.modalContent.querySelector(`.thumb-container .swiper-slide[data-media-id="${key}"]`);
        const tIdx = tTarget ? tSlides.indexOf(tTarget) : -1;
        if (tIdx >= 0) {
          const tUseLoop = !!thumbEl.swiper.params.loop;
          if (tUseLoop && typeof thumbEl.swiper.slideToLoop === 'function') {
            thumbEl.swiper.slideToLoop(tIdx);
          } else {
            thumbEl.swiper.slideTo(tIdx);
          }
        }
      }
    }
  });
}
